import Card from '@rio-cloud/rio-uikit/Card';
import { convertISOToRelativeDate, getFullAddressLabel } from '../../common/utils';
import type { Address, ItineraryDetails } from '../../store/facade/facadeApiV3';
import type { TourStatus } from '../../store/facade/facadeApiV3';

type TourCardProps = {
    tourId?: string;
    itinerarySummary?: ItineraryDetails;
    departureAddress?: Address;
    arrivalAddress?: Address;
    locale: string;
    isOnGoing?: boolean;
    status?: TourStatus;
    onClick?: () => void;
};

const getArrivalDate = (itinerarySummary?: ItineraryDetails) => {
    if (itinerarySummary?.arrivalEvent?.estimatedDay) {
        return itinerarySummary.arrivalEvent.estimatedDay;
    }
    if (itinerarySummary?.departureEvent?.estimatedDay && itinerarySummary?.totalDuration) {
        const departure = new Date(itinerarySummary.departureEvent.estimatedDay);
        return new Date(departure.getTime() + itinerarySummary.totalDuration * 1000).toISOString();
    }
    return undefined;
};

export const TourCard = ({
    itinerarySummary,
    departureAddress,
    arrivalAddress,
    locale,
    isOnGoing,
    status,
    onClick,
}: TourCardProps) => (
    <Card
        role='button'
        tabIndex={0}
        className='max-width-600 cursor-pointer hover-bg-lightest transition-all'
        data-testid='tour-card'
        onClick={onClick}
        onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
            }
        }}
    >
        <div className='display-flex column-gap-10 justify-content-between'>
            <div data-testid='tour-card-departure'>
                <span className='text-size-20 rioglyph rioglyph-start line-height-20 text-color-gray margin-bottom-5' />
                <div className='text-medium'>
                    {convertISOToRelativeDate(locale, itinerarySummary?.departureEvent?.estimatedDay)}
                </div>
                <div className='text-size-h6'>{getFullAddressLabel(departureAddress)}</div>
            </div>
            {isOnGoing ? (
                <span className='text-size-20 rioglyph rioglyph-tire spinning text-size-150pct align-self-center' />
            ) : (
                <span className='text-size-20 rioglyph rioglyph-arrow-right line-height-20 text-color-gray align-self-center' />
            )}
            <div data-testid='tour-card-arrival'>
                <span className='text-size-20 rioglyph rioglyph-finish line-height-20 text-color-gray margin-bottom-5' />
                <div className='text-medium'>{convertISOToRelativeDate(locale, getArrivalDate(itinerarySummary))}</div>
                <div className='text-size-h6'>{getFullAddressLabel(arrivalAddress)}</div>
            </div>
        </div>
        {status && (
            <div className='display-flex justify-content-center margin-top-10' data-testid='tour-card-status'>
                <span className='text-size-12 text-color-gray text-uppercase'>{status}</span>
            </div>
        )}
    </Card>
);
